import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Car, Wrench, Bike, Truck, Send, Loader2, Sparkles, CheckCircle, Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import WorldAutoLogo from './WorldAutoLogo';
import FranceText from './FranceText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default footer settings
const DEFAULTS = {
  footer_company_name: 'World Auto Pro',
  footer_email: 'contact@worldautofrance.com',
  footer_description: '',
  footer_copyright: '© {year} World Auto Pro. Tous droits réservés.',
  footer_show_newsletter: true,
  footer_newsletter_title: 'Restez informé',
  footer_show_categories: true,
  footer_show_links: true,
  footer_show_contact: true,
  footer_bg_color: '',
  footer_text_color: '',
  footer_link_color: '',
  footer_accent_color: '',
  footer_show_social: true,
  footer_facebook: '',
  footer_instagram: '',
  footer_twitter: '',
  footer_linkedin: '',
};

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [settings, setSettings] = useState(DEFAULTS);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings({ ...DEFAULTS, ...res.data });
      } catch (err) {
        // Use defaults
      }
    };
    fetchSettings();
  }, []);

  const s = settings;

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setSubscribed(true);
      setEmail('');
      toast.success('Inscription réussie !');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'inscription';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic styles
  const footerStyle = {
    backgroundColor: s.footer_bg_color || undefined,
    color: s.footer_text_color || undefined,
  };

  const linkStyle = {
    color: s.footer_link_color || undefined,
  };

  const copyrightText = (s.footer_copyright || '© {year} World Auto Pro. Tous droits réservés.')
    .replace('{year}', new Date().getFullYear().toString());

  return (
    <footer 
      className="bg-primary text-primary-foreground mt-auto" 
      data-testid="footer"
      style={footerStyle}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Logo & Description */}
          <div className="col-span-1 lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4 group">
              <WorldAutoLogo className="w-10 h-10 transition-transform group-hover:scale-110" />
              <span className="font-heading font-bold text-xl">{s.footer_company_name || 'World Auto Pro'}</span>
            </Link>
            <p className="text-primary-foreground/70 text-sm leading-relaxed mb-6" style={linkStyle}>
              {s.footer_description || t('footer.description')}
            </p>

            {/* Newsletter Form */}
            {s.footer_show_newsletter !== false && (
              <div className="bg-primary-foreground/10 rounded-lg p-4">
                <h4 className="font-heading font-bold text-sm mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-accent" />
                  {s.footer_newsletter_title || t('footer.newsletter')}
                </h4>
                {subscribed ? (
                  <div className="flex items-center gap-2 text-sm text-green-400">
                    <CheckCircle className="w-4 h-4" />
                    {t('common.success')}!
                  </div>
                ) : (
                  <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                    <Input
                      type="email"
                      placeholder={t('footer.subscribe_placeholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 text-sm h-9"
                      required
                    />
                    <Button 
                      type="submit" 
                      size="sm"
                      className="bg-accent hover:bg-accent/90 h-9 px-3"
                      disabled={loading}
                      style={{ backgroundColor: s.footer_accent_color || undefined }}
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </form>
                )}
              </div>
            )}

            {/* Social Media Links */}
            {s.footer_show_social !== false && (s.footer_facebook || s.footer_instagram || s.footer_twitter || s.footer_linkedin) && (
              <div className="flex gap-3 mt-4">
                {s.footer_facebook && (
                  <a href={s.footer_facebook} target="_blank" rel="noopener noreferrer" className="text-primary-foreground/70 hover:text-accent transition-colors">
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {s.footer_instagram && (
                  <a href={s.footer_instagram} target="_blank" rel="noopener noreferrer" className="text-primary-foreground/70 hover:text-accent transition-colors">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {s.footer_twitter && (
                  <a href={s.footer_twitter} target="_blank" rel="noopener noreferrer" className="text-primary-foreground/70 hover:text-accent transition-colors">
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
                {s.footer_linkedin && (
                  <a href={s.footer_linkedin} target="_blank" rel="noopener noreferrer" className="text-primary-foreground/70 hover:text-accent transition-colors">
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Catégories */}
          {s.footer_show_categories !== false && (
          <div>
            <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <Car className="w-5 h-5 text-accent" />
              {t('nav.categories')}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link to="/annonces/pieces" className="text-primary-foreground/70 hover:text-accent transition-colors flex items-center gap-2 group" style={linkStyle}>
                  <Wrench className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                  {t('nav.parts')}
                </Link>
              </li>
              <li>
                <Link to="/annonces/voitures" className="text-primary-foreground/70 hover:text-accent transition-colors flex items-center gap-2 group" style={linkStyle}>
                  <Car className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  {t('nav.cars')}
                </Link>
              </li>
              <li>
                <Link to="/annonces/motos" className="text-primary-foreground/70 hover:text-accent transition-colors flex items-center gap-2 group" style={linkStyle}>
                  <Bike className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  {t('nav.motorcycles')}
                </Link>
              </li>
              <li>
                <Link to="/annonces/utilitaires" className="text-primary-foreground/70 hover:text-accent transition-colors flex items-center gap-2 group" style={linkStyle}>
                  <Truck className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  {t('nav.utilities')}
                </Link>
              </li>
            </ul>
          </div>
          )}

          {/* Liens utiles */}
          {s.footer_show_links !== false && (
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">{t('footer.useful_links')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/nouveautes" className="text-primary-foreground/70 hover:text-accent transition-colors flex items-center gap-1" style={linkStyle}>
                  <Sparkles className="w-3 h-3" />
                  {t('nav.updates')}
                </Link>
              </li>
              <li>
                <Link to="/a-propos" className="text-primary-foreground/70 hover:text-accent transition-colors" style={linkStyle}>
                  {t('footer.about')}
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-primary-foreground/70 hover:text-accent transition-colors" style={linkStyle}>
                  {t('footer.faq')}
                </Link>
              </li>
              <li>
                <Link to="/cgv" className="text-primary-foreground/70 hover:text-accent transition-colors" style={linkStyle}>
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <Link to="/politique-retours" className="text-primary-foreground/70 hover:text-accent transition-colors" style={linkStyle}>
                  {t('footer.returns')}
                </Link>
              </li>
              <li>
                <Link to="/mentions-legales" className="text-primary-foreground/70 hover:text-accent transition-colors" style={linkStyle}>
                  {t('footer.legal')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-primary-foreground/70 hover:text-accent transition-colors" style={linkStyle}>
                  {t('footer.contact')}
                </Link>
              </li>
            </ul>
          </div>
          )}

          {/* Contact */}
          {s.footer_show_contact !== false && (
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">{t('footer.contact')}</h3>
            <a 
              href={`mailto:${s.footer_email || 'contact@worldautofrance.com'}`}
              className="flex items-center gap-3 text-primary-foreground/70 hover:text-accent transition-colors group"
              style={linkStyle}
            >
              <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center group-hover:bg-accent/30 transition-colors">
                <Mail className="w-5 h-5 text-accent" />
              </div>
              <span className="text-sm">{s.footer_email || 'contact@worldautofrance.com'}</span>
            </a>
          </div>
          )}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-primary-foreground/20 mt-10 pt-8">
          <p className="text-primary-foreground/50 text-sm text-center" style={linkStyle}>
            {copyrightText}
          </p>
          <p className="text-primary-foreground/40 text-xs text-center mt-2 max-w-md mx-auto">
            Aucune partie de ce site ne peut être reproduite ou transmise sous quelque forme ou par quelque moyen que ce soit sans notre autorisation.
          </p>
        </div>
      </div>
    </footer>
  );
}
